import { api } from "@/common/lib/api";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

export type LactationPhase = "first_lactation" | "peak" | "late" | "cull";

export type ReferenceValueRow = {
  id: string;
  category: string;
  lactationPhase: LactationPhase | null;
  minValueCents: number;
  maxValueCents: number;
  updatedAt: string;
  updatedBy: string;
};

export type PricePerKgRow = {
  id: string;
  category: string;
  minPricePerKgCents: number;
  maxPricePerKgCents: number;
  updatedAt: string;
  updatedBy: string;
};

export function useReferenceValues(accountId: string) {
  return useQuery({
    queryKey: ["reference-values", accountId],
    queryFn: () =>
      api.get<{ values: ReferenceValueRow[] }>(`/accounts/${accountId}/reference-tables/values`),
    enabled: Boolean(accountId),
  });
}

export function usePricePerKg(accountId: string) {
  return useQuery({
    queryKey: ["price-per-kg", accountId],
    queryFn: () =>
      api.get<{ prices: PricePerKgRow[] }>(`/accounts/${accountId}/reference-tables/prices`),
    enabled: Boolean(accountId),
  });
}

export function useUpdateReferenceValue(accountId: string) {
  const qc = useQueryClient();
  return useMutation<
    ReferenceValueRow,
    Error,
    { id: string; minValueCents: number; maxValueCents: number }
  >({
    mutationFn: ({ id, minValueCents, maxValueCents }) =>
      api.patch<ReferenceValueRow>(`/accounts/${accountId}/reference-tables/values/${id}`, {
        minValueCents,
        maxValueCents,
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["reference-values", accountId] });
      // Cambiar un valor deja la valuación desactualizada → refrescar el aviso.
      void qc.invalidateQueries({ queryKey: ["appreciation-stale", accountId] });
    },
  });
}

export function useUpdatePricePerKg(accountId: string) {
  const qc = useQueryClient();
  return useMutation<
    PricePerKgRow,
    Error,
    { id: string; minPricePerKgCents: number; maxPricePerKgCents: number }
  >({
    mutationFn: ({ id, minPricePerKgCents, maxPricePerKgCents }) =>
      api.patch<PricePerKgRow>(`/accounts/${accountId}/reference-tables/prices/${id}`, {
        minPricePerKgCents,
        maxPricePerKgCents,
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["price-per-kg", accountId] });
      // Cambiar un precio deja la valuación desactualizada → refrescar el aviso.
      void qc.invalidateQueries({ queryKey: ["appreciation-stale", accountId] });
    },
  });
}

// ─── Apreciación: aviso "desactualizado" + recompute ────────────────────────
// internal/specs/recalcular-apreciacion.md. Cambiar un valor referencial no
// recalcula la apreciación sola; el usuario dispara el recompute desde el aviso.

export type RecomputeAppreciationResult = {
  monthsRecomputed: number;
  animalsProcessed: number;
  eventsGenerated: number;
  errors: { animalId: string; message: string }[];
};

export function useAppreciationStale(accountId: string) {
  return useQuery({
    queryKey: ["appreciation-stale", accountId],
    queryFn: () => api.get<{ stale: boolean }>(`/accounts/${accountId}/animals/appreciation/stale`),
    enabled: Boolean(accountId),
  });
}

export function useRecomputeAppreciation(accountId: string) {
  const qc = useQueryClient();
  return useMutation<{ result: RecomputeAppreciationResult }, Error, void>({
    mutationFn: () =>
      api.post<{ result: RecomputeAppreciationResult }>(
        `/accounts/${accountId}/animals/appreciation/recompute`,
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["appreciation-stale", accountId] });
      // La valuación (valor en libros) puede haber cambiado.
      void qc.invalidateQueries({ queryKey: ["depreciation", accountId] });
    },
  });
}
